import { ICallTranscriptTurn } from '@principle-theorem/principle-core/interfaces';

export class CallTranscript {
  /**
   * Locates speaker markers within a flat call transcript.
   *
   * A marker (`Bot:` or `User:`) is recognised only at the start of the string
   * or when preceded by whitespace, via a non-consuming lookbehind — so colons
   * inside a turn (e.g. `Your appointment: 4 pm`) never split it, and two markers
   * separated only by whitespace (an empty turn, `Bot: User: hi`) still split,
   * because the lookbehind does not consume the separator. The trailing `\s*`
   * absorbs the space between a marker and its text.
   */
  private static readonly _markerRegex = /(?<=^|\s)(Bot|User):\s*/g;

  /**
   * Splits a flat call transcript string into ordered speaker turns.
   *
   * Text between a marker and the next marker belongs to that marker's speaker
   * and is trimmed. `Bot:` maps to `'agent'` and `User:` maps to `'caller'`.
   * Any text before the first marker is dropped.
   *
   * Returns `undefined` when the transcript contains no markers, or when parsing
   * yields no non-empty turns, so callers can fall back to rendering the raw
   * transcript string.
   */
  static parse(transcript: string): ICallTranscriptTurn[] | undefined {
    const markers = [...transcript.matchAll(CallTranscript._markerRegex)];
    if (markers.length === 0) {
      return undefined;
    }

    const turns = markers
      .map((marker, index): ICallTranscriptTurn => {
        const speaker = marker[1] === 'Bot' ? 'agent' : 'caller';
        const textStart = (marker.index ?? 0) + marker[0].length;
        const textEnd = markers[index + 1]?.index ?? transcript.length;
        return { speaker, text: transcript.slice(textStart, textEnd).trim() };
      })
      .filter((turn) => turn.text.length > 0);

    return turns.length > 0 ? turns : undefined;
  }
}
